<template>
  <div class="image-gallery d-flex flex-column align-items-stretch gap-2">
    <div class="position-relative rounded w-100 gallery-main">
      <b-image
        v-if="imageUrls.length > 0"
        :src="imageUrl"
        auto-size
        style="cursor: pointer"
        class="w-100 h-100"
        image-class="rounded w-100 h-100"
      />
      <button
        v-if="imageUrls.length > 0"
        type="button"
        class="default-toggle-button"
        @click.stop="setDisplayedAsPrimary"
      >
        <i :class="`bi-${isDisplayedPrimary ? 'star-fill' : 'star'}`" />
        <span>{{ isDisplayedPrimary ? 'Default' : 'Set as default' }}</span>
      </button>
      <div v-if="imageUrls.length > 1" class="main-nav-overlay d-flex flex-row align-items-center justify-content-between px-1 position-absolute top-0 start-0 end-0 bottom-0">
        <div class="bg-white rounded nav-arrow-wrap">
          <icon-button small icon="chevron-left" @click.stop="selectImage((displayIndex - 1 + imageUrls.length) % imageUrls.length, false, true)"/>
        </div>
        <div class="bg-white rounded nav-arrow-wrap">
          <icon-button small icon="chevron-right" @click.stop="selectImage((displayIndex + 1) % imageUrls.length, false, true)"/>
        </div>
      </div>
    </div>

    <div class="thumbnail-rail position-relative">
      <button
        type="button"
        class="thumb-scroll-arrow thumb-scroll-arrow-left"
        :disabled="!canScrollLeft"
        @click="scrollThumbnails(-1)"
      >
        <i class="bi-chevron-left" />
      </button>

      <div
        ref="thumbnailScroller"
        :class="['thumbnail-scroller d-flex flex-row gap-1 align-items-stretch', {'is-dragging':isDraggingThumbRail}]"
        @scroll="updateThumbScrollState"
        @pointerdown="onThumbPointerDown"
        @pointermove="onThumbPointerMove"
        @pointerup="onThumbPointerUp"
        @pointercancel="onThumbPointerCancel"
      >
        <button
          type="button"
          v-for="(image, index) in imageUrls"
          :key="`${image}-${index}`"
          ref="thumbnails"
          class="thumbnail-button"
          @click="event => onThumbnailClick(event, index)"
        >
          <img
            :src="image"
            :class="['rounded h-100 gallery-image', {active:index === displayIndex}]"
            style="object-fit:cover"
            alt=""
            draggable="false"
            @load="updateThumbScrollState"
          />
          <div v-if="image === primaryImage" class="thumbnail-primary-indicator">
            <i class="bi-star-fill" />
          </div>
        </button>
      </div>

      <button
        type="button"
        class="thumb-scroll-arrow thumb-scroll-arrow-right"
        :disabled="!canScrollRight"
        @click="scrollThumbnails(1)"
      >
        <i class="bi-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import BImage from "@/components/common/BImage.vue";
import IconButton from "@/components/common/IconButton.vue";

const DRAG_THRESHOLD_PX = 14;

export default {
  components: {IconButton, BImage},
  props: {
    imageUrls: Array,
    primaryImage: String
  },
  emits: ['update:primaryImage'],
  data() {
    return {
      displayIndex: 0,
      canScrollLeft: false,
      canScrollRight: false,
      isDraggingThumbRail: false,
      activePointerId: null,
      dragStartX: 0,
      dragStartScrollLeft: 0,
      dragMoved: false,
      suppressClickUntil: 0
    };
  },
  watch: {
    imageUrls: {
      immediate: true,
      handler() {
        this.syncDisplayIndex();
      }
    },
    primaryImage() {
      if (!this.imageUrls.length) return;
      const index = this.imageUrls.indexOf(this.primaryImage);
      if (index >= 0) {
        this.displayIndex = index;
        this.$nextTick(() => {
          this.scrollThumbnailIntoView(index, 'smooth');
          this.updateThumbScrollState();
        });
      }
    }
  },
  computed: {
    imageUrl() {
      return this.imageUrls[this.displayIndex];
    },
    isDisplayedPrimary() {
      return this.imageUrl && this.imageUrl === this.primaryImage;
    }
  },
  methods: {
    selectImage(index, setPrimary = false, scrollTo = false) {
      if (index < 0 || index >= this.imageUrls.length) {
        return;
      }
      this.displayIndex = index;
      if (setPrimary) {
        this.$emit('update:primaryImage', this.imageUrl);
      }
      if (scrollTo) {
        this.$nextTick(() => {
          this.scrollThumbnailIntoView(index, 'smooth');
          this.updateThumbScrollState();
        });
      }
    },
    setDisplayedAsPrimary() {
      if (!this.imageUrl || this.isDisplayedPrimary) {
        return;
      }
      this.$emit('update:primaryImage', this.imageUrl);
    },
    syncDisplayIndex() {
      const count = this.imageUrls.length;
      if (!count) {
        this.displayIndex = 0;
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }
      if (this.displayIndex >= count || this.displayIndex < 0) {
        const primaryIndex = this.imageUrls.indexOf(this.primaryImage);
        this.displayIndex = primaryIndex >= 0 ? primaryIndex : 0;
      }
      this.$nextTick(() => {
        this.scrollThumbnailIntoView(this.displayIndex, 'auto');
        this.updateThumbScrollState();
      });
    },
    updateThumbScrollState() {
      const scroller = this.$refs.thumbnailScroller;
      if (!scroller) return;
      const epsilon = 2;
      this.canScrollLeft = scroller.scrollLeft > epsilon;
      this.canScrollRight = (scroller.scrollLeft + scroller.clientWidth) < (scroller.scrollWidth - epsilon);
    },
    scrollThumbnails(direction) {
      const scroller = this.$refs.thumbnailScroller;
      if (!scroller) return;
      const step = Math.max(160, scroller.clientWidth * 0.8);
      scroller.scrollBy({
        left: direction * step,
        behavior: 'smooth'
      });
    },
    scrollThumbnailIntoView(index, behavior = 'smooth') {
      const scroller = this.$refs.thumbnailScroller;
      const image = this.$refs.thumbnails?.[index];
      if (!scroller || !image) return;

      const viewLeft = scroller.scrollLeft;
      const viewRight = viewLeft + scroller.clientWidth;
      const targetLeft = image.offsetLeft;
      const targetRight = targetLeft + image.offsetWidth;

      if (targetLeft < viewLeft) {
        scroller.scrollTo({left: Math.max(0, targetLeft - 12), behavior});
      } else if (targetRight > viewRight) {
        scroller.scrollTo({left: targetRight - scroller.clientWidth + 12, behavior});
      }
    },
    onThumbnailClick(event, index) {
      if (Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        return;
      }
      this.selectImage(index, false, false);
    },
    onThumbPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }
      const scroller = this.$refs.thumbnailScroller;
      if (!scroller) return;

      this.isDraggingThumbRail = true;
      this.activePointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = scroller.scrollLeft;
      this.dragMoved = false;
    },
    onThumbPointerMove(event) {
      if (!this.isDraggingThumbRail || event.pointerId !== this.activePointerId) {
        return;
      }
      const scroller = this.$refs.thumbnailScroller;
      if (!scroller) return;

      const delta = event.clientX - this.dragStartX;
      if (!this.dragMoved && Math.abs(delta) > DRAG_THRESHOLD_PX) {
        this.dragMoved = true;
      }
      if (!this.dragMoved) {
        return;
      }
      scroller.scrollLeft = this.dragStartScrollLeft - delta;
      this.updateThumbScrollState();
      event.preventDefault();
    },
    endThumbDrag() {
      if (this.dragMoved) {
        this.suppressClickUntil = Date.now() + 140;
      }
      this.isDraggingThumbRail = false;
      this.activePointerId = null;
      this.dragMoved = false;
    },
    onThumbPointerUp(event) {
      if (event.pointerId !== this.activePointerId) return;
      this.endThumbDrag();
    },
    onThumbPointerCancel(event) {
      if (event.pointerId !== this.activePointerId) return;
      this.endThumbDrag();
    },
    onWindowPointerUp(event) {
      if (!this.isDraggingThumbRail || event.pointerId !== this.activePointerId) {
        return;
      }
      this.endThumbDrag();
    },
    onWindowPointerCancel(event) {
      if (!this.isDraggingThumbRail || event.pointerId !== this.activePointerId) {
        return;
      }
      this.endThumbDrag();
    }
  },
  mounted() {
    const primaryIndex = this.imageUrls.indexOf(this.primaryImage);
    this.displayIndex = primaryIndex >= 0 ? primaryIndex : 0;
    this.$nextTick(() => {
      this.updateThumbScrollState();
    });
    window.addEventListener('pointerup', this.onWindowPointerUp);
    window.addEventListener('pointercancel', this.onWindowPointerCancel);
  },
  beforeUnmount() {
    window.removeEventListener('pointerup', this.onWindowPointerUp);
    window.removeEventListener('pointercancel', this.onWindowPointerCancel);
  }
}
</script>

<style>
.gallery-main {
  height: 300px;
}

.gallery-image {
  transition: opacity 180ms ease;
  opacity: 0.5;
}
.gallery-image.active {
  opacity: 1;
}

.default-toggle-button {
  position: absolute;
  left: 0.75rem;
  bottom: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(15, 23, 42, 0.42);
  color: #fff;
  border-radius: 0.5rem;
  padding: 0.3rem 0.55rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  line-height: 1;
  backdrop-filter: blur(3px);
  transition: background-color 120ms ease, border-color 120ms ease;
  z-index: 3;
}
.default-toggle-button:hover,
.default-toggle-button:focus {
  background: rgba(15, 23, 42, 0.58);
  border-color: rgba(255, 255, 255, 0.5);
}

.thumbnail-rail {
  position: relative;
}

.thumbnail-scroller {
  height: 100px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  cursor: grab;
  touch-action: pan-x;
  scroll-behavior: smooth;
}
.thumbnail-scroller.is-dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}
.thumbnail-scroller::-webkit-scrollbar {
  display: none;
}

.thumbnail-button {
  flex: 0 0 auto;
  height: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  position: relative;
  user-select: none;
}

.main-nav-overlay {
  pointer-events: none;
  z-index: 2;
}

.nav-arrow-wrap {
  pointer-events: auto;
}

.thumb-scroll-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.88);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  transition: opacity 120ms ease, background-color 120ms ease;
  z-index: 2;
}
.thumb-scroll-arrow:hover:not(:disabled),
.thumb-scroll-arrow:focus:not(:disabled) {
  background: rgba(255, 255, 255, 0.97);
}
.thumb-scroll-arrow:disabled {
  opacity: 0.35;
}
.thumb-scroll-arrow-left {
  left: 0.25rem;
}
.thumb-scroll-arrow-right {
  right: 0.25rem;
}

.thumbnail-primary-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.5);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  pointer-events: none;
}
</style>
